<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Purchase',
  },
})

const { can } = useAbility()
const { loading: deleteLoading, request: deleteRequest } = useApiRequest()

// ─── State ────────────────────────────────────────────────────────────────────
const search = ref('')
const filterStatus = ref(null)
const page = ref(1)
const perPage = ref(15)
const loading = ref(false)
const orders = ref([])
const totalItems = ref(0)

// ─── Constants ────────────────────────────────────────────────────────────────
const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Draft', value: 'draft' },
  { title: 'Dikirim', value: 'sent' },
  { title: 'Sebagian Diterima', value: 'partial' },
  { title: 'Selesai', value: 'received' },
  { title: 'Dibatalkan', value: 'cancelled' },
]

const statusColor = {
  draft:     'default',
  sent:      'info',
  partial:   'warning',
  received:  'success',
  cancelled: 'error',
}

const statusLabel = {
  draft:     'Draft',
  sent:      'Dikirim',
  partial:   'Sebagian',
  received:  'Selesai',
  cancelled: 'Dibatalkan',
}

const headers = [
  { title: 'No. PO', key: 'po_number', sortable: false },
  { title: 'Supplier', key: 'supplier', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Total', key: 'total_amount', sortable: false, align: 'end' },
  { title: 'Tgl. Order', key: 'ordered_at', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 100 },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatRupiah = val =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val ?? 0)

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'

// ─── Fetch ────────────────────────────────────────────────────────────────────
const fetchOrders = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value)    query.search = search.value
    if (filterStatus.value) query.status = filterStatus.value

    const response = await useApi('/api/v1/purchase-orders', { query }).get()
    const body = response?.data ?? response

    orders.value     = body?.data ?? []
    totalItems.value = body?.meta?.total ?? 0
  } catch (err) {
    console.error('[PurchaseOrders] fetch error', err)
    orders.value = []
  } finally {
    loading.value = false
  }
}

watch([search, filterStatus, page], fetchOrders, { immediate: true })

// ─── Hapus PO (hanya draft) ───────────────────────────────────────────────────
const deleteDialog = ref(false)
const orderToDelete = ref(null)

const openDeleteDialog = order => {
  orderToDelete.value = order
  deleteDialog.value  = true
}

const confirmDelete = async () => {
  const result = await deleteRequest(() =>
    useApi(`/api/v1/purchase-orders/${orderToDelete.value.id}`).delete()
  )

  if (result !== null) {
    deleteDialog.value = false
    orderToDelete.value = null
    await fetchOrders()
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardText class="d-flex flex-wrap gap-4 align-center justify-space-between">
          <div class="d-flex gap-3 flex-wrap align-center">
            <VTextField
              v-model="search"
              placeholder="Cari no. PO..."
              prepend-inner-icon="ri-search-line"
              density="compact"
              hide-details
              clearable
              style="min-width: 200px"
            />
            <VSelect
              v-model="filterStatus"
              :items="statusOptions"
              label="Status"
              density="compact"
              hide-details
              style="min-width: 170px"
            />
          </div>

          <VBtn
            v-if="can('create', 'Purchase')"
            :to="{ name: 'purchase-orders-create' }"
            prepend-icon="ri-add-line"
          >
            Buat PO
          </VBtn>
        </VCardText>

        <VDivider />

        <VDataTableServer
          v-model:page="page"
          :headers="headers"
          :items="orders"
          :items-length="totalItems"
          :items-per-page="perPage"
          :loading="loading"
          no-data-text="Belum ada purchase order."
        >
          <template #item.supplier="{ item }">
            {{ item.supplier?.name ?? '-' }}
          </template>

          <template #item.status="{ item }">
            <VChip :color="statusColor[item.status]" size="small">
              {{ statusLabel[item.status] ?? item.status }}
            </VChip>
          </template>

          <template #item.total_amount="{ item }">
            {{ formatRupiah(item.total_amount) }}
          </template>

          <template #item.ordered_at="{ item }">
            {{ formatDate(item.ordered_at) }}
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex gap-1 justify-center">
              <IconBtn :to="{ name: 'purchase-orders-id', params: { id: item.id } }">
                <VIcon icon="ri-eye-line" />
                <VTooltip activator="parent">Detail</VTooltip>
              </IconBtn>
              <IconBtn
                v-if="can('update', 'Purchase') && item.is_editable"
                :to="{ name: 'purchase-orders-id-edit', params: { id: item.id } }"
              >
                <VIcon icon="ri-pencil-line" />
                <VTooltip activator="parent">Edit</VTooltip>
              </IconBtn>
              <IconBtn
                v-if="can('delete', 'Purchase') && item.is_editable"
                @click="openDeleteDialog(item)"
              >
                <VIcon icon="ri-delete-bin-line" color="error" />
                <VTooltip activator="parent">Hapus</VTooltip>
              </IconBtn>
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </VCol>
  </VRow>

  <!-- ─── Hapus PO Confirmation Dialog ────────────────────────────────────── -->
  <VDialog v-model="deleteDialog" max-width="440" persistent>
    <VCard>
      <VCardTitle class="pt-5 px-5">Hapus Purchase Order?</VCardTitle>
      <VCardText class="px-5">
        PO <strong>{{ orderToDelete?.po_number }}</strong> beserta semua itemnya akan
        dihapus permanen dan tidak bisa dipulihkan. Hanya PO berstatus Draft yang bisa
        dihapus — kalau sudah dikirim ke supplier, gunakan "Batalkan PO" sebagai gantinya.
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn variant="tonal" :disabled="deleteLoading" @click="deleteDialog = false">
          Batal
        </VBtn>
        <VBtn color="error" :loading="deleteLoading" @click="confirmDelete">
          Ya, Hapus
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
